import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Inbox, Check, CheckCheck, Users, Plug, RefreshCw, UserCheck, ArrowRight } from "lucide-react";
import { sr } from "../../lib/sr";

const KIND_UI = {
  partner_intro: { label: "Partner introduction", icon: Users, cls: "sr-badge-gold" },
  setup_request: { label: "Setup request", icon: Plug, cls: "sr-badge-amber" },
  client_approval: { label: "Client account request", icon: UserCheck, cls: "sr-badge-green" },
};
const TYPE_FILTERS = [
  { key: "all", label: "All types" },
  { key: "client_approval", label: "Account approvals" },
  { key: "partner_intro", label: "Partner intros" },
  { key: "setup_request", label: "Setup requests" },
];

export default function OpsTasks() {
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("open");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = (st = statusFilter) => {
    setData(null);
    setSelected([]);
    sr.adminOpsTasks(st).then(setData).catch((e) => {
      if (e?.response?.status === 403) toast.error("Staff only");
      setData({ tasks: [], open_count: 0 });
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const resolve = async (t) => {
    setBusy(t.id);
    try {
      await sr.resolveOpsTask(t.id, { source: t.source, tenant_id: t.tenant_id, connector_key: t.connector_key, channel: t.channel });
      toast.success("Marked done");
      load();
    } catch { toast.error("Could not update"); } finally { setBusy(""); }
  };

  if (!data) return <div className="sr-empty" style={{ padding: 60, textAlign: "center" }}><Loader2 className="sr-spin" /></div>;

  const tasks = (data.tasks || []).filter((t) => typeFilter === "all" || t.kind === typeFilter);
  const openTasks = tasks.filter((t) => t.status !== "done");
  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = openTasks.length > 0 && openTasks.every((t) => selected.includes(t.id));
  const toggleAll = () => setSelected(allSelected ? [] : openTasks.map((t) => t.id));
  const resolveSelected = async () => {
    const chosen = openTasks.filter((t) => selected.includes(t.id));
    if (!chosen.length) return;
    setBulkBusy(true);
    try {
      for (const t of chosen) {
        await sr.resolveOpsTask(t.id, { source: t.source, tenant_id: t.tenant_id, connector_key: t.connector_key, channel: t.channel });
      }
      toast.success(`Marked ${chosen.length} done`);
      setSelected([]); load();
    } catch { toast.error("Some items could not be updated"); load(); } finally { setBulkBusy(false); }
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "8px 4px 40px" }} data-testid="ops-tasks-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <h1 style={{ color: "var(--navy)", fontSize: 26, margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Inbox size={22} /> Ops task inbox {data.open_count > 0 && <span className="sr-badge sr-badge-amber" data-testid="ops-open-count">{data.open_count} open</span>}
        </h1>
        <div style={{ display: "flex", gap: 6 }}>
          <Link className="sr-btn sr-btn-sm" to="/app/admin/client-approvals" data-testid="ops-to-approvals"><UserCheck size={13} /> Client approvals</Link>
          <button className={`sr-btn sr-btn-sm ${statusFilter === "open" ? "sr-btn-primary" : ""}`} onClick={() => setStatusFilter("open")} data-testid="ops-filter-open">Open</button>
          <button className={`sr-btn sr-btn-sm ${statusFilter === "all" ? "sr-btn-primary" : ""}`} onClick={() => setStatusFilter("all")} data-testid="ops-filter-all">All</button>
          <button className="sr-btn sr-btn-sm" onClick={() => load()} data-testid="ops-refresh"><RefreshCw size={13} /></button>
        </div>
      </div>
      <p className="sr-muted" style={{ marginTop: 6 }}>New client account requests, partner introductions and connection setup requests — so nothing slips through.</p>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} data-testid="ops-type-filters">
          {TYPE_FILTERS.map((f) => (
            <button key={f.key} className={`sr-btn sr-btn-sm ${typeFilter === f.key ? "sr-btn-primary" : ""}`} onClick={() => { setTypeFilter(f.key); setSelected([]); }} data-testid={`ops-type-${f.key}`}>{f.label}</button>
          ))}
        </div>
        {openTasks.length > 0 && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 13, color: "var(--navy)", cursor: "pointer" }}>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="ops-select-all" /> Select all
            </label>
            <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={resolveSelected} disabled={bulkBusy || selected.length === 0} data-testid="ops-bulk-resolve">
              {bulkBusy ? <Loader2 size={13} className="sr-spin" /> : <><CheckCheck size={14} /> Mark {selected.length || ""} done</>}
            </button>
          </div>
        )}
      </div>

      {tasks.length === 0 ? (
        <div className="sr-card sr-empty" style={{ marginTop: 14, textAlign: "center", padding: 30 }} data-testid="ops-empty">Nothing here — you're all caught up.</div>
      ) : (
        <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
          {tasks.map((t) => {
            const ui = KIND_UI[t.kind] || KIND_UI.setup_request;
            const Icon = ui.icon;
            const isOpen = t.status !== "done";
            return (
              <div key={t.id} className="sr-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, opacity: t.status === "done" ? 0.6 : 1 }} data-testid={`ops-task-${t.id}`}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flex: 1 }}>
                  {isOpen && (
                    <input type="checkbox" style={{ marginTop: 4 }} checked={selected.includes(t.id)} onChange={() => toggleSel(t.id)} data-testid={`ops-select-${t.id}`} />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span className={`sr-badge ${ui.cls}`} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon size={12} /> {ui.label}</span>
                      <strong style={{ color: "var(--navy)" }}>{t.tenant_name}</strong>
                    </div>
                    <div style={{ fontSize: 14, marginTop: 4 }}>{t.title}</div>
                    {t.detail && <div className="sr-muted" style={{ fontSize: 12, marginTop: 2 }}>{t.detail}</div>}
                  </div>
                </div>
                {t.status === "done" ? (
                  <span className="sr-badge sr-badge-green"><Check size={12} /> Done</span>
                ) : (
                  <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => resolve(t)} disabled={busy === t.id} data-testid={`ops-resolve-${t.id}`}>
                    {busy === t.id ? <Loader2 size={13} className="sr-spin" /> : (t.kind === "client_approval" ? <><UserCheck size={13} /> Approve</> : <><Check size={13} /> Mark done</>)}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
